import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, Tag, PieChart, Eye, PenLine, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Reveal, PageHero, SectionHeading, ClosingCTA } from "@/components/marketing/primitives";
import { MarketingFAQ } from "@/components/marketing/MarketingFAQ";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { startPlanCheckout } from "@/lib/billing";
import { savePendingPlan, PLAN_NAMES } from "@/lib/pendingPlan";
import { linesByPlan, PLAN_ORDER, CAPABILITIES } from "@/lib/planCatalog";

const ENTERPRISE_LINES = ["Discuss your requirements", "Investor and team capacity", "Onboarding and support needs", "Security and procurement review", "Custom scope agreed with your team"];

export function PricingTiers({ compact = false, subscription = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [annual, setAnnual] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [showCoupon, setShowCoupon] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const [plansError, setPlansError] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true); setPlansError(false);
    try { const {data}=await api.get("/plans"); if (!data.plans?.length) throw new Error("Empty catalog"); setPlans(data.plans); }
    catch { setPlansError(true); }
    finally { setPlansLoading(false); }
  }, []);
  useEffect(() => { loadPlans(); }, [loadPlans]);

  const checkout = async (planKey) => {
    if (planKey === "starter") { navigate(user ? "/app" : "/register"); return; }
    if (planKey === "enterprise") { navigate("/contact"); return; }
    if (!user) {
      savePendingPlan(planKey, annual ? "yearly" : "monthly");
      toast.info(`Create your founder account first. ${PLAN_NAMES[planKey]} (${annual ? "annual" : "monthly"}) stays selected for you.`);
      navigate("/register"); return;
    }
    if (user.role !== "founder") { toast.error("Only founder accounts can subscribe."); return; }
    const lookup = `${planKey}_${annual ? "yearly" : "monthly"}`;
    setLoadingKey(planKey);
    try {
      await startPlanCheckout(lookup, coupon.trim() || undefined);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  const byKey = Object.fromEntries(plans.map((p) => [p.key, p]));
  const features = { ...linesByPlan(plans), enterprise: ENTERPRISE_LINES };
  const priceFor = (key) => {
    const p = byKey[key];
    if (!p) return null;
    if (annual) return p.price_yearly ? Number((p.price_yearly / 12).toFixed(2)) : 0;
    return p.price_monthly || 0;
  };

  const tiers = [
    { key: "starter", name: "Starter", cta: user ? "Go to dashboard" : "Start free", highlight: false, priceLabel: "$0" },
    { key: "growth", name: "Growth", cta: "Choose Growth", highlight: true },
    { key: "scale", name: "Scale", cta: "Choose Scale", highlight: false },
    { key: "fund_manager", name: "Fund Manager", cta: "Choose Fund Manager", highlight: false },
    { key: "enterprise", name: "Enterprise", cta: "Contact sales", highlight: false, priceLabel: "Custom" },
  ];

  return (
    <div data-testid="pricing-tiers">
      {plansError && <div role="alert" data-testid="pricing-load-error" className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-red-200 bg-red-50 p-4 text-sm text-red-800">Current prices could not load.<Button variant="outline" onClick={loadPlans} disabled={plansLoading} data-testid="pricing-retry">Try again</Button></div>}
      <div className="mb-8 flex flex-col items-center gap-4">
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1" data-testid="billing-toggle">
          <button onClick={() => setAnnual(false)} aria-pressed={!annual} className={`ff-manrope rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${!annual ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="toggle-monthly">Monthly</button>
          <button onClick={() => setAnnual(true)} aria-pressed={annual} className={`ff-manrope rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${annual ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="toggle-annual">Annual <span className={annual ? "text-brand" : "text-brand-ink"}>Save ~17%</span></button>
        </div>
        {!subscription?.has_subscription && (!showCoupon ? (
          <button onClick={() => setShowCoupon(true)} className="ff-manrope flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="show-coupon-btn"><Tag className="h-3.5 w-3.5" /> Have a coupon?</button>
        ) : (
          <div className="flex items-center gap-2">
            <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="COUPON CODE" className="h-9 w-44 uppercase" data-testid="coupon-input" />
            <span className="ff-manrope text-xs text-slate-400">applied at checkout</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {tiers.map((t) => {
          const monthly = priceFor(t.key);
          const price = t.priceLabel ?? (monthly == null ? "-" : `$${monthly}`);
          const current = subscription?.has_subscription && subscription.lookup_key === `${t.key}_${annual ? "yearly" : "monthly"}`;
          return (
            <div key={t.key} data-testid={`plan-card-${t.key}`}
              className={`flex flex-col rounded-xl border p-6 ${t.highlight ? "border-slate-900 bg-slate-900 text-white shadow-xl" : "border-slate-200 bg-white"}`}>
                {t.highlight && <span data-testid={`plan-popular-${t.key}`} className="mb-3 self-start rounded-full bg-brand px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-foreground">Most popular</span>}
              <h3 className={`ff-cabinet text-xl font-bold tracking-tight ${t.highlight ? "text-white" : "text-slate-900"}`}>{t.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span data-testid={`plan-price-${t.key}`} className={`ff-cabinet text-4xl font-black tracking-tighter ${t.highlight ? "text-white" : "text-slate-900"}`}>{price}</span>
                {!t.priceLabel && <span className={`ff-manrope text-sm font-medium ${t.highlight ? "text-slate-300" : "text-slate-500"}`}>/mo</span>}
              </div>
              <p data-testid={`plan-billing-${t.key}`} className={`ff-manrope mt-1 min-h-4 text-xs font-medium ${t.highlight ? "text-slate-300" : "text-slate-500"}`}>
                {!t.priceLabel && annual && byKey[t.key] ? `$${byKey[t.key].price_yearly} billed annually` : (!t.priceLabel ? "billed monthly" : "\u00A0")}
              </p>
              <Button onClick={() => checkout(t.key)} disabled={!!loadingKey || current || (!t.priceLabel && (plansLoading || plansError))} data-testid={`plan-cta-${t.key}`}
                  className={`ff-manrope mt-5 w-full gap-1.5 rounded-md font-semibold ${t.highlight ? "bg-brand text-brand-foreground hover:bg-brand-hover" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                {loadingKey === t.key ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {current ? "Current plan" : t.cta}
              </Button>
              <ul className="mt-6 space-y-2.5" data-testid={`plan-features-${t.key}`}>
                {(features[t.key] || []).map((f, i) => (
                  <li key={i} data-testid={`plan-feature-${t.key}-${i}`} className={`ff-manrope flex items-start gap-2 text-sm font-medium ${t.highlight ? "text-slate-200" : "text-slate-600"} ${i === 0 && t.key !== "starter" ? "font-semibold" : ""}`}>
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${t.highlight ? "text-brand" : "text-brand-ink"}`} strokeWidth={2.2} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PlanCompareTable() {
  const [plans, setPlans] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => setFailed(true)); }, []);
  if (failed) return <p className="text-sm text-slate-500" data-testid="plan-compare-error">The plan comparison could not load. The cards above show the current catalog.</p>;
  if (!plans) return <p className="text-sm text-slate-500" data-testid="plan-compare-loading">Loading plan comparison…</p>;
  const byKey = Object.fromEntries(plans.map((p) => [p.key, p]));
  const cols = PLAN_ORDER.map((k) => byKey[k]).filter(Boolean);
  const rows = [
    ["Investors", (p) => `Up to ${p.investors.toLocaleString()}`],
    ["AI-drafted updates", (p) => (p.ai_updates_per_month == null ? "Unlimited" : `${p.ai_updates_per_month} / month`)],
    ["Team seats", (p) => String(p.team_seats)],
    ["Employee equity pages", (p) => (p.equity_pages ? String(p.equity_pages) : false)],
    ["Funds & SPVs", (p) => (p.funds ? (p.max_funds == null ? "Unlimited" : String(p.max_funds)) : false)],
    ...CAPABILITIES.filter(([key]) => key !== "funds").map(([key, label]) => [label, (p) => !!p[key]]),
    ["Monthly price", (p) => (p.price_monthly ? `$${p.price_monthly}` : "$0")],
    ["Annual price", (p) => (p.price_yearly ? `$${p.price_yearly}` : "$0")],
  ];
  const cell = (value, planKey, label) => {
    const id = `compare-${planKey}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    if (value === true) return <Check className="mx-auto h-4 w-4 text-brand-ink" aria-label="Included" data-testid={id} />;
    if (value === false) return <span className="text-slate-300" aria-label="Not included" data-testid={id}>-</span>;
    return <span data-testid={id}>{value}</span>;
  };
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="plan-compare-table">
      <table className="ff-manrope w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left">
            <th scope="col" className="px-4 py-3 font-semibold text-slate-600">What you get</th>
            {cols.map((p) => <th key={p.key} scope="col" className="px-4 py-3 text-center font-semibold text-slate-900">{p.name}</th>)}
            <th scope="col" className="px-4 py-3 text-center font-semibold text-slate-900">Enterprise</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, read]) => (
            <tr key={label} className="border-b border-slate-100 last:border-0">
              <th scope="row" className="px-4 py-3 text-left font-medium text-slate-700">{label}</th>
              {cols.map((p) => <td key={p.key} className="px-4 py-3 text-center text-slate-700">{cell(read(p), p.key, label)}</td>)}
              <td className="px-4 py-3 text-center text-slate-500">Custom</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STANDALONE_TOOLS = [
  { icon: PieChart, name: "Cap-table & equity platform", price: 250, note: "issue shares, model rounds, 409A add-ons" },
  { icon: Eye, name: "Deck & data-room tracking tool", price: 65, note: "page-by-page view analytics, secure rooms" },
  { icon: PenLine, name: "E-signature tool", price: 40, note: "per-user seats, envelope limits" },
];

export function ToolStackComparison() {
  const [selected, setSelected] = useState(STANDALONE_TOOLS.map(() => true));
  const toggle = (i) => setSelected((s) => s.map((v, idx) => (idx === i ? !v : v)));

  const stackTotal = STANDALONE_TOOLS.reduce((s, t, i) => s + (selected[i] ? t.price : 0), 0);
  const [tenxPrice, setTenxPrice] = useState(null);
  useEffect(() => { api.get("/plans").then(({data}) => setTenxPrice(data.plans.find((p)=>p.key==="scale")?.price_monthly ?? null)).catch(()=>{}); }, []);
  const savingMo = Math.max(0, stackTotal - tenxPrice);
  const savingPct = stackTotal > 0 ? Math.round((savingMo / stackTotal) * 100) : 0;
  const anySelected = selected.some(Boolean);

  return (
    <div data-testid="tool-stack-comparison" className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-[1fr_auto_1fr]">
      {/* Stitched-together stack */}
      <div className="flex flex-col rounded-2xl border border-slate-200 bg-slate-50 p-6">
        <div className="mb-4 flex items-center justify-between">
          <span className="ff-manrope text-xs font-bold uppercase tracking-wide text-slate-500">Tools you'd buy separately</span>
          <span className="ff-manrope rounded-full bg-slate-200 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">tick what you pay for</span>
        </div>
        <div className="space-y-2.5">
          {STANDALONE_TOOLS.map((t, i) => (
            <button
              key={t.name}
              type="button"
              onClick={() => toggle(i)}
              data-testid={`standalone-tool-row-${i}`}
              aria-pressed={selected[i]}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${selected[i] ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-100 opacity-60"}`}
            >
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${selected[i] ? "border-neutral-900 bg-neutral-900 text-white" : "border-slate-300 bg-white text-transparent"}`}>
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><t.icon className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1">
                  <span className="ff-manrope block text-sm font-semibold text-slate-800">{t.name}</span>
                  <span className="ff-manrope block text-xs text-slate-500">{t.note}</span>
              </span>
              <span className="ff-cabinet shrink-0 text-sm font-bold text-slate-700">~${t.price}<span className="ff-manrope text-xs font-medium text-slate-400">/mo</span></span>
            </button>
          ))}
        </div>
        <div className="mt-auto flex items-baseline justify-between border-t border-slate-200 pt-4">
          <span className="ff-manrope text-sm font-semibold text-slate-600">Your combined bill</span>
          <span className="ff-cabinet text-3xl font-black tracking-tighter text-slate-900" data-testid="stack-total">~${stackTotal}<span className="ff-manrope text-sm font-medium text-slate-400">/mo</span></span>
        </div>
        <ul className="mt-4 space-y-1.5">
          {["Separate logins, exports & billing", "Investor data lives in separate silos", "No single source of truth"].map((c) => (
            <li key={c} className="ff-manrope flex items-start gap-2 text-xs font-medium text-slate-500">
              <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" strokeWidth={2.5} />{c}
            </li>
          ))}
        </ul>
      </div>

      {/* vs */}
      <div className="flex items-center justify-center">
        <span className="ff-cabinet flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-black uppercase text-slate-400 shadow-sm">vs</span>
      </div>

      {/* TenX */}
      <div className="relative flex flex-col overflow-hidden rounded-2xl border border-slate-900 bg-slate-900 p-6 text-white shadow-xl">
        <div className="TenX-dotgrid pointer-events-none absolute inset-0 opacity-10" />
        <div className="relative flex h-full flex-col">
          <div className="mb-4 flex items-center justify-between">
            <span className="ff-manrope text-xs font-bold uppercase tracking-wide text-brand">TenX, one platform</span>
            <span className="ff-manrope inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-0.5 text-[11px] font-bold text-brand-foreground"><Sparkles className="h-3 w-3" /> All-in-one</span>
          </div>
          <ul className="space-y-2.5">
            {[
              "Cap table, rounds & dilution modeling",
              "Deck & data-room sharing with view tracking",
              "Document Builder + e-signatures",
              "Investor updates, CRM & impact intelligence",
            ].map((f,i) => (
              <li key={f} data-testid={`tenx-feature-row-${i}`} className="ff-manrope flex items-start gap-2 text-sm font-medium text-slate-200">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" strokeWidth={2.4} />{f}
              </li>
            ))}
          </ul>
          <div className="mt-auto">
            <div className="mt-6 flex items-baseline justify-between border-t border-white/10 pt-4">
              <span className="ff-manrope text-sm font-semibold text-slate-300">Scale plan</span>
              <span className="ff-cabinet text-3xl font-black tracking-tighter text-white" data-testid="TenX-price">{tenxPrice == null ? "Unavailable" : `$${tenxPrice}`}<span className="ff-manrope text-sm font-medium text-slate-400">/mo</span></span>
            </div>
            <div className="mt-4 rounded-xl bg-white/10 px-4 py-3 text-center" data-testid="stack-savings">
              {tenxPrice !== null && anySelected && savingMo > 0 ? (
                <>
                  <span className="ff-cabinet text-lg font-bold text-brand">Save ~${savingMo}/mo</span>
                  <span className="ff-manrope ml-2 text-sm text-slate-300">({savingPct}% less, ~${savingMo * 12}/yr)</span>
                </>
              ) : (
                <span className="ff-manrope text-sm text-slate-300">Tick the tools you use to see your saving.</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  return (
    <MarketingLayout>
      <PageHero eyebrow="Plans" title="Start free." accent="Pay when your investor base grows." subtitle="Every plan includes the investor portal, AI-assisted updates, and your choice of delivery. Investors always read, comment, and sign for free." />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <PricingTiers />
        <p className="ff-manrope mt-10 text-center text-sm text-slate-400" data-testid="pricing-tax-note">Prices in USD. Cancel or change plans anytime. Plan limits and features are read from the live catalog.</p>
      </section>
      <section className="border-t border-slate-200 bg-slate-50" data-testid="pricing-compare-section">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8">
          <SectionHeading eyebrow="Compare plans" title="Every limit and capability, side by side." description="The same catalog that powers checkout and plan gating in the product." id="pricing-compare" />
          <PlanCompareTable />
        </div>
      </section>
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8">
          <SectionHeading eyebrow="The case for connected" title="One workflow. Fewer separate tools." description="Explore an illustrative tool budget alongside TenX Scale. Your actual costs depend on the services and plans you use." id="pricing-comparison" />
          <Reveal i={3}><ToolStackComparison /></Reveal>
          <p className="ff-manrope mx-auto mt-8 max-w-3xl text-center text-xs text-slate-400" data-testid="comparison-disclaimer">
            Standalone costs are illustrative planning assumptions, not verified vendor quotes or guaranteed savings. Pricing varies by company stage, seats, add-ons, and contract. TenX Scale pricing is loaded from the current catalog. Features and allowances differ between products.
          </p>
        </div>
      </section>
      <MarketingFAQ id="pricing-faq" items={[["Can I change or cancel my plan?","Workspace owners can manage an eligible paid subscription through Billing. Subscription cancellation does not delete your account or company data."],["Are multiple companies included in one subscription?","Each company is a separate workspace with its own plan and billing. Creating another company starts a new Starter workspace; an existing paid subscription is not copied."],["How does annual billing work?","Annual plans are paid upfront for the year. The monthly figure is an equivalent rate; the total annual charge is displayed underneath each plan."],["Do investors pay?","No. Investors do not need a founder subscription to read the information shared with them, comment, or sign."]]} />
      <ClosingCTA id="pricing-contact" to="/contact" label="Talk to us" title="Not sure which plan fits?" description="Tell us how many investors you report to, who is on your team, and how often you send updates. We will point you to the right plan." />
    </MarketingLayout>
  );
}
